import * as React from "react";

const noop = () => undefined;

export function stickyOffset(element: Element) {
  const top = parseFloat(getComputedStyle(element).top);
  if (!Number.isNaN(top)) return top;
  const value = getComputedStyle(element).getPropertyValue("--app-header-height").trim();
  const rem = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
  const amount = parseFloat(value);
  if (Number.isNaN(amount)) return 0;
  return value.endsWith("rem") ? amount * rem : amount;
}

export function useStuck<T extends HTMLElement>(enabled = true) {
  const ref = React.useRef<T>(null);
  const naturalTop = React.useRef(0);
  const offset = React.useRef<number | null>(null);

  const subscribe = React.useCallback(
    (onChange: () => void) => {
      if (!enabled) return noop;
      window.addEventListener("scroll", onChange, { passive: true });
      window.addEventListener("resize", onChange);
      return () => {
        window.removeEventListener("scroll", onChange);
        window.removeEventListener("resize", onChange);
      };
    },
    [enabled]
  );

  const stuck = React.useSyncExternalStore(
    subscribe,
    () => {
      const element = ref.current;
      if (!enabled || !element) return false;
      offset.current ??= stickyOffset(element);
      const top = element.getBoundingClientRect().top;
      const pinned = window.scrollY > 0 && top <= offset.current + 0.5;
      if (!pinned) naturalTop.current = top + window.scrollY;
      return pinned;
    },
    () => false
  );

  const readNaturalTop = React.useCallback(() => naturalTop.current, []);
  return { ref, stuck, naturalTop: readNaturalTop };
}
